import React, { useEffect, useMemo, useState } from 'react';
import { Card, Descriptions, Typography } from 'antd';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useNavigate } from 'react-router-dom';

import ProposalService from '../services/ProposalService';
import PhoneNumberDisplay from './PhoneNumberDisplay';

const { Text } = Typography;

const DEFAULT_LAT = 41.0082;
const DEFAULT_LNG = 28.9784;

const defaultIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const formatAddressPart = (value?: string | null): string => {
  const trimmed = value?.trim();
  return trimmed ? trimmed.toLocaleUpperCase('tr-TR') : '';
};

type ProposalLike = {
  id?: number;
  proposalNumber?: string;
  company?: any;
  address?: {
    title?: string | null;
    addressLine1?: string | null;
    addressLine2?: string | null;
    state?: string | null;
    city?: string | null;
    phone?: string | null;
    sgkNumber?: string | null;
    latitude?: number | null;
    longitude?: number | null;
  } | null;
};

interface ControlAddressCardProps {
  proposalId?: number | null;
  proposal?: ProposalLike | null;
  title?: React.ReactNode;
  accordionOpen?: boolean;
  onAccordionToggle?: () => void;
  className?: string;
  style?: React.CSSProperties;
  onProposalLoaded?: (proposal: ProposalLike | null) => void;
  fetchOnMount?: boolean;
  loading?: boolean;
}

const ControlAddressCard: React.FC<ControlAddressCardProps> = ({
  proposalId,
  proposal: proposalProp,
  title = 'Firma Bilgileri',
  accordionOpen,
  onAccordionToggle,
  className,
  style,
  onProposalLoaded,
  fetchOnMount = true,
  loading: loadingProp,
}) => {
  const navigate = useNavigate();
  const [internalLoading, setInternalLoading] = useState<boolean>(false);
  const [proposal, setProposal] = useState<ProposalLike | null>(proposalProp ?? null);
  const numericProposalId = useMemo(() => {
    if (proposalId == null) return null;
    const num = Number(proposalId);
    return Number.isFinite(num) && num > 0 ? num : null;
  }, [proposalId]);
  const isLoading = loadingProp ?? internalLoading;
  const isAccordionMode = typeof accordionOpen === 'boolean' && typeof onAccordionToggle === 'function';
  const isExpanded = !isAccordionMode || Boolean(accordionOpen);


  const descriptionLabelStyle = useMemo<React.CSSProperties>(
    () => ({
      width: 200,
      maxWidth: 200,
      fontWeight: 600,
      color: 'rgba(0, 0, 0, 0.88)',
    }),
    [],
  );

  useEffect(() => {
    setProposal(proposalProp ?? null);
  }, [proposalProp]);

  useEffect(() => {
    if (!fetchOnMount) {
      return;
    }
    if (!numericProposalId) {
      return;
    }
    if (proposalProp && Number(proposalProp.id) === numericProposalId) {
      return;
    }

    let ignore = false;
    const load = async () => {
      try {
        setInternalLoading(true);
        const response = await ProposalService.get(numericProposalId);
        if (ignore) {
          return;
        }
        const data = (response as any)?.data ?? response ?? null;
        setProposal(data);
        onProposalLoaded?.(data ?? null);
      } catch (error) {
        if (!ignore) {
          console.error('ControlAddressCard: failed to load proposal', error);
          setProposal(null);
          onProposalLoaded?.(null);
        }
      } finally {
        if (!ignore) {
          setInternalLoading(false);
        }
      }
    };
    load();
    return () => {
      ignore = true;
    };
  }, [numericProposalId, proposalProp, onProposalLoaded, fetchOnMount]);

  const address = proposal?.address ?? null;
  const latitude = typeof address?.latitude === 'number' ? address.latitude : null;
  const longitude = typeof address?.longitude === 'number' ? address.longitude : null;
  const mapCenter = useMemo<[number, number]>(() => {
    if (latitude != null && longitude != null) {
      return [latitude, longitude];
    }
    return [DEFAULT_LAT, DEFAULT_LNG];
  }, [latitude, longitude]);

  return (
    <Card
      title={
        isAccordionMode ? (
          <div
            role='button'
            tabIndex={0}
            onClick={onAccordionToggle}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onAccordionToggle?.();
              }
            }}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          >
            <span>{title}</span>
            <span>{isExpanded ? '-' : '+'}</span>
          </div>
        ) : (
          title
        )
      }
      className={className}
      style={style}
      loading={isLoading}
    >
      {isExpanded && !isLoading && proposal ? (
        <>
          {/* {proposal?.proposalNumber ? (
            <Text strong style={{ display: 'inline-block', marginBottom: 12 }}>
              Teklif No: {proposal.proposalNumber}
            </Text>
          ) : null} */}
          <Descriptions
            column={1}
            bordered
            size='small'
          >
            {proposal?.company?.id && proposal?.company?.name ? (
              <Descriptions.Item
                label='Firma'
                labelStyle={{ width: 200, maxWidth: 200, fontWeight: 600, color: 'rgba(0, 0, 0, 0.88)' }}
              >
                <span
                  style={{ cursor: 'pointer', color: '#1677ff' }}
                  onClick={(event) => {
                    event.preventDefault();
                    navigate(`/companies/${proposal.company!.id}`);
                  }}
                  title='Firma detayına git'
                >
                  {proposal?.company?.name}
                </span>
              </Descriptions.Item>
            ) : null}
            <Descriptions.Item
              label='Adres Başlığı'
              labelStyle={{ width: 200, maxWidth: 200, fontWeight: 600, color: 'rgba(0, 0, 0, 0.88)' }}
            >
              {latitude != null && longitude != null && address?.title ? (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  title="Google Haritalar'da aç"
                  style={{ color: '#1677ff', cursor: 'pointer' }}
                >
                  {address.title}
                </a>
              ) : (
                <span style={{ color: 'rgba(0, 0, 0, 0.88)' }}>{address?.title}</span>
              )}
            </Descriptions.Item>
            <Descriptions.Item
              label='Adres'
              labelStyle={{ width: 200, maxWidth: 200, fontWeight: 600, color: 'rgba(0, 0, 0, 0.88)' }}
            >
              <span style={{ color: 'rgba(0, 0, 0, 0.88)' }}>
                {address?.addressLine1}
                {address?.addressLine2 ? ` ${address.addressLine2}` : ''}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label='İlçe / İl' labelStyle={descriptionLabelStyle}>
              {`${formatAddressPart(address?.state)} / ${formatAddressPart(address?.city)}`}
            </Descriptions.Item>
            <Descriptions.Item label='Telefon' labelStyle={descriptionLabelStyle}>
              {address?.phone ? (
                <PhoneNumberDisplay value={address?.phone} />
              ) : (
                ''
              )}
            </Descriptions.Item>
            <Descriptions.Item label='E-Posta' labelStyle={descriptionLabelStyle}>
              {proposal?.company?.email}
            </Descriptions.Item>
            <Descriptions.Item label='Vergi Dairesi' labelStyle={descriptionLabelStyle}>
              {proposal?.company?.taxOffice}
            </Descriptions.Item>
            <Descriptions.Item label='Vergi Numarası' labelStyle={descriptionLabelStyle}>
              {proposal?.company?.taxNumber}
            </Descriptions.Item>
            <Descriptions.Item label='SGK/DETSİS Numarası' labelStyle={descriptionLabelStyle}>
              {address?.sgkNumber}
            </Descriptions.Item>
          </Descriptions>
          <div style={{ height: 320, borderRadius: 8, overflow: 'hidden', marginTop: 12 }}>
            <MapContainer
              center={mapCenter}
              zoom={latitude != null && longitude != null ? 12 : 8}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
              />
              {latitude != null && longitude != null ? (
                <Marker position={[latitude, longitude]} icon={defaultIcon} />
              ) : null}
            </MapContainer>
          </div>
        </>
      ) : isExpanded && !isLoading && numericProposalId ? (
        <Text>Adres bilgisi bulunamadı.</Text>
      ) : isExpanded && !isLoading ? (
        <Text>Geçerli bir teklif seçilmedi.</Text>
      ) : null}
    </Card>
  );
};

export default ControlAddressCard;
